import {
    CallExpressionShape,
    type Database,
    type Diagnostics,
    type Factory,
    type ObjectShape,
    type Record as SdkRecord,
} from '@servicenow/sdk-build-core'
import {
    addFieldsToColumn,
    COLUMN_API_TO_TYPE,
    COLUMN_TYPE_TO_API,
    getDefaultMaxLength,
    getDocumentationRecords,
} from '@servicenow/sdk-build-plugins/column'
const GenericColumn = { name: 'GenericColumn' as const }

/**
 * Build sys_pd_process_input or sys_pd_process_output records from a playbook
 * declarations config. Handles StringColumn and ReferenceColumn only — the
 * column types supported in the PAD designer UI.
 */
export async function buildPlaybookVariableRecords(options: {
    variablesConfig: ObjectShape
    parentRecord: SdkRecord
    factory: Factory
    diagnostics: Diagnostics
    parentTable: string
    varTable: string
}): Promise<SdkRecord[]> {
    const { variablesConfig, parentRecord, factory, diagnostics, parentTable, varTable } = options

    const records: SdkRecord[] = []
    const tableName = `var__m_${varTable}_${parentRecord.getId().getValue()}`
    const entries = variablesConfig.entries()

    for (const [i, [k, v]] of entries.entries()) {
        const callExpr = v.as(CallExpressionShape)
        const config = callExpr.getArgument(0).asObject()

        const orderFromShape = config.get('order')?.ifNumber()?.getValue()
        const internalType =
            callExpr.getCallee() === GenericColumn.name
                ? (config.get('columnType')?.ifString()?.getValue() ?? 'string')
                : (COLUMN_API_TO_TYPE[callExpr.getCallee() as keyof typeof COLUMN_API_TO_TYPE] ?? 'string')

        const maxLength = config.get('maxLength')?.ifNumber()?.getValue() ?? getDefaultMaxLength(internalType)
        const referenceTable =
            internalType === 'reference' || internalType === 'records'
                ? config.get('referenceTable')?.ifString()?.getValue()
                : undefined

        const props = config.transform(({ $ }) => ({
            element: $.val(k),
            label: $.def(k),
            mandatory: $.toBoolean().def(false),
            active: $.val(true),
            order: $.val(orderFromShape ?? (i + 1) * 100),
            hint: $.def(''),
            model: $.val(parentRecord),
            model_id: $.val(parentRecord),
            model_table: $.val(parentTable),
            name: $.val(tableName),
            default_value: $.from('default').def(''),
            internal_type: $.val(internalType),
            max_length: $.val(maxLength),
            attributes: $.val(''),
            ...(referenceTable ? { reference: $.val(referenceTable) } : {}),
        }))

        const varRecord = await factory.createRecord({
            source: callExpr,
            table: varTable,
            properties: props,
        })

        const updatedCallExpr = addFieldsToColumn({ name: k, table: tableName, display: false }, callExpr)
        const docRecords = await getDocumentationRecords(updatedCallExpr, {}, factory, diagnostics, true)
        records.push(...docRecords)

        records.push(varRecord)
    }

    return records
}

/**
 * Build CallExpressionShapes from sys_pd_process_input or sys_pd_process_output
 * records for the toShape (XML → Fluent) direction.
 */
export function buildPlaybookVariableShapes(
    variableRecords: SdkRecord[],
    descendants: Database
): Record<string, CallExpressionShape> {
    const shapes: Record<string, CallExpressionShape> = {}

    const sorted = [...variableRecords].sort((a, b) => {
        const aOrder = Number(a.get('order')?.ifString()?.getValue() ?? 0)
        const bOrder = Number(b.get('order')?.ifString()?.getValue() ?? 0)
        return aOrder - bOrder
    })

    for (const variable of sorted) {
        const name = variable.get('element').asString().getValue()
        const internalType = variable.get('internal_type').asString().getValue()
        const callExpressionName =
            COLUMN_TYPE_TO_API[internalType as keyof typeof COLUMN_TYPE_TO_API] ?? GenericColumn.name

        const variableName = variable.get('name').ifString()?.getValue()
        const documentation = variableName
            ? descendants.query('sys_documentation', { name: variableName, element: name })
            : []
        const hint = documentation[0]?.get('hint')?.ifString()?.getValue() ?? ''

        const defaultMaxLength = getDefaultMaxLength(internalType)
        const maxLength = Number(variable.get('max_length').asString().getValue())

        const orderValue = Number(variable.get('order')?.ifString()?.getValue() ?? 0)
        const referenceTable = variable.get('reference')?.ifString()?.getValue()

        shapes[name] = new CallExpressionShape({
            source: variable,
            callee: callExpressionName,
            args: [
                variable.transform(({ $ }) => ({
                    label: $,
                    mandatory: $.toBoolean().def(false),
                    maxLength: $.val(maxLength).def(defaultMaxLength),
                    default: $.from('default_value').def(''),
                    ...(hint ? { hint: $.val(hint) } : {}),
                    ...(orderValue > 0 ? { order: $.val(orderValue) } : {}),
                    ...(referenceTable && (internalType === 'reference' || internalType === 'records')
                        ? { referenceTable: $.val(referenceTable) }
                        : {}),
                })),
            ],
        })
    }

    return shapes
}
